// Core types for Import/Export functionality
export interface ImportExportConfig {
  entityType: string;
  dealerId?: string;
  supportedFormats: ('csv' | 'json' | 'zip')[];
  maxFileSize?: string;
  customValidation?: (data: any) => ValidationResult;
  customTransform?: (data: any) => any;
  exportFields?: string[];
  importFields?: string[];
}


export interface ValidationConfig {
  requiredFields?: string[];
  uniqueFields?: string[];
  customRules?: ValidationRule[];
}

export interface ValidationRule {
  field: string;
  rule: 'email' | 'phone' | 'date' | 'number' | 'custom';
  message: string;
  customValidator?: (value: any) => boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  row?: number;
}

export interface ValidationWarning {
  field: string;
  message: string;
  row?: number;
}

export interface ImportResult {
  type?: 'import';
  success: boolean;
  importedCount: number;
  updatedCount?: number;
  failedCount: number;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  downloadUrl?: string;
}

export interface ExportResult {
  success: boolean;
  downloadUrl: string;
  fileName: string;
  recordCount: number;
  fileSize: number;
}

export interface ComparisonResult {
  newRecords: any[];
  existingRecords: any[];
  conflicts: ConflictRecord[];
  summary: {
    totalNew: number;
    totalExisting: number;
    totalConflicts: number;
  };
}

// New compare data structure based on API response
export interface CompareDataItem {
  current: any;
  incoming: any;
  _showOnlyConflicts?: boolean;
  _conflictingFields?: Record<string, any>;
}

export interface CompareResponse {
  body: CompareDataItem[];
}

export interface ConflictRecord {
  field: string;
  currentValue: any;
  incomingValue: any;
  row: number;
  conflictType: 'value_mismatch' | 'new_field' | 'missing_field';
}

// Pre-configured entity types (like DataGridTypes static methods)
export class ImportExportTypes {
  static DEALERS = (dealerId?: string): ImportExportConfig => ({
    entityType: 'dealers',
    dealerId,
    supportedFormats: ['csv', 'json', 'zip'],
    maxFileSize: '10MB'
  });

  static OFFERS = (dealerId?: string): ImportExportConfig => ({
    entityType: 'offers',
    dealerId,
    supportedFormats: ['zip', 'csv', 'json'],
    maxFileSize: '50MB'
  });

  static USERS = (): ImportExportConfig => ({
    entityType: 'users',
    supportedFormats: ['csv', 'json'],
    maxFileSize: '5MB'
  });

  static LEADS = (dealerId?: string): ImportExportConfig => ({
    entityType: 'leads',
    dealerId,
    supportedFormats: ['csv', 'json', 'zip'],
    maxFileSize: '50MB'
  });

  static SEGMENTS = (dealerId?: string): ImportExportConfig => ({
    entityType: 'segments',
    dealerId,
    supportedFormats: ['csv', 'json'],
    maxFileSize: '10MB'
  });

  // MIA Chat specific entities
  static MIA_CAMPAIGNS = (dealerId?: string): ImportExportConfig => ({
    entityType: 'mia_campaigns',
    dealerId,
    supportedFormats: ['csv', 'json'],
    maxFileSize: '10MB'
  });

  static MIA_TEMPLATES = (dealerId?: string): ImportExportConfig => ({
    entityType: 'mia_templates',
    dealerId,
    supportedFormats: ['csv', 'json'],
    maxFileSize: '5MB'
  });

  static RUN_LOGS = (dealerId?: string): ImportExportConfig => ({
    entityType: 'run_logs',
    dealerId,
    supportedFormats: ['csv', 'json'],
    maxFileSize: '20MB'
  });

  static ROLES = (): ImportExportConfig => ({
    entityType: 'roles',
    supportedFormats: ['csv', 'json'],
    maxFileSize: '2MB'
  });

  static SCOPES = (): ImportExportConfig => ({
    entityType: 'scopes',
    supportedFormats: ['csv', 'json'],
    maxFileSize: '2MB'
  });

  static RESOURCES = (): ImportExportConfig => ({
    entityType: 'resources',
    supportedFormats: ['csv', 'json'],
    maxFileSize: '2MB'
  });
}
